import React, {useState} from 'react'
import {
  Row,
  Col,
  H5,
  Caption,
  ExtraSmall,
  TextAreaPure,
  withInputContainer,
  CustomerDialogButtonRow,
  PencilIcon,
  DangerAlert,
  ButtonLink,
} from '@modifi/ui'
import {semiDarkGray} from '@modifi/colors'
import {useTranslation} from '@modifi/plugin-i18n'
import {Form, FormActionsContext} from '@modifi/plugin-forms'
import {FormInputWrapper} from '@modifi/plugin-onboarding'

import {getFullNameFromEmail} from '../../../../utils/getNameFromEmail'
import {TaskUpdate} from '../../constants'
import {NoteCol} from './styles'

const TextAreaPureWithInputContainer = withInputContainer(TextAreaPure)

export type InternalNoteProps = {
  params: {
    internalNote?: string
    internalNoteModifiedBy?: string
    taskId: string
    isEditAction?: boolean
    canEdit?: boolean
  }
  isUpdatePending?: boolean
  discard: () => void
  submit: (taskId: string, changes: TaskUpdate, actionMessage?: string) => void
  errorMessage?: string
}

const InternalNoteDialog: React.FC<InternalNoteProps> = ({
  params: {internalNote, internalNoteModifiedBy, taskId, isEditAction, canEdit},
  isUpdatePending = false,
  discard,
  submit,
  errorMessage = '',
}) => {
  const {t} = useTranslation('kartavya')
  const [isEditMode, setIsEditMode] = useState(isEditAction || !internalNote)

  const handleSubmit = (changes: {internalNote?: string}) => {
    submit && submit(taskId, {internalNote: changes.internalNote})
  }

  const onDiscard = () => {
    if (internalNote && isEditMode) {
      setIsEditMode(false)
    }
    discard()
  }

  return (
    <>
      {errorMessage && (
        <Row mb={16}>
          <Col>
            <DangerAlert>{errorMessage}</DangerAlert>
          </Col>
        </Row>
      )}
      <Row alignItems="center" justifyContent="center">
        <Col auto>
          <H5 textAlign="center">{t('tasks.internal-note-dialog.headline')}</H5>
        </Col>
      </Row>
      {!isEditMode ? (
        <Row mt={32}>
          <NoteCol>
            <Row alignItems="center">
              <Col col={10}>
                <Caption>{internalNote}</Caption>
              </Col>
              {canEdit && (
                <Col col={1}>
                  <ButtonLink
                    data-target="internal-note-edit"
                    onClick={() => setIsEditMode(true)}
                    transparent
                  >
                    <PencilIcon />
                  </ButtonLink>
                </Col>
              )}
            </Row>
          </NoteCol>
        </Row>
      ) : (
        <Form initialValues={{internalNote}} onSubmit={handleSubmit} isLoading={isUpdatePending}>
          <Row mt={32}>
            <Col>
              <FormInputWrapper
                name="internalNote"
                inputComponent={TextAreaPureWithInputContainer}
                placeholder={t('tasks.add-note-dialog.internal-note.placeholder')}
                optional
                label={t('tasks.add-note-dialog.internal-note.label')}
                required={false}
                t={t}
                rows={5}
              />
            </Col>
          </Row>
          <FormActionsContext.Consumer>
            {ctx => (
              <CustomerDialogButtonRow
                canSubmit={ctx.canSubmit && !isUpdatePending && ctx.hasChanges}
                onSubmitClick={ctx.submit}
                submitText={t('tasks.add-note-dialog.cta-submit')}
                hasCancel
                canCancel={!isUpdatePending}
                cancelText={t('tasks.add-note-dialog.cta-cancel')}
                onCancelClick={onDiscard}
                loading={isUpdatePending}
              />
            )}
          </FormActionsContext.Consumer>
        </Form>
      )}
      {!isEditMode && (
        <Row mt={24}>
          <Col>
            <ExtraSmall color={semiDarkGray}>
              {getFullNameFromEmail(internalNoteModifiedBy || '')}
            </ExtraSmall>
          </Col>
        </Row>
      )}
    </>
  )
}

export default InternalNoteDialog
